import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { collection, getDocs, doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { validateArchetypeConditions } from '../../utils/rulesEngine';

function ArchetypesManager() {
  const [archetypes, setArchetypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingArchetype, setEditingArchetype] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);

  useEffect(() => {
    loadArchetypes();
  }, []);

  const loadArchetypes = async () => {
    setLoading(true);
    try {
      const archetypesSnap = await getDocs(collection(db, 'archetypes'));
      const archetypesData = [];
      archetypesSnap.forEach((doc) => {
        archetypesData.push({ id: doc.id, ...doc.data() });
      });
      setArchetypes(archetypesData);
    } catch (error) {
      console.error('Error loading archetypes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (archetype) => {
    setEditingArchetype({
      ...archetype,
      conditionsJson: JSON.stringify(archetype.conditions || {}, null, 2),
      famousMindsText: (archetype.famousMinds || []).join(', '),
    });
    setEditDialogOpen(true);
    setSaveStatus(null);
  };

  const handleCreate = () => {
    setEditingArchetype({
      id: '',
      name: '',
      enabled: true,
      isDefault: false,
      conditionsJson: '{\n  \n}',
      reflectionQuestion: '',
      famousMindsText: '',
      careerVectors: {
        natural: '',
        latent: '',
      },
      customInstructions: '',
    });
    setEditDialogOpen(true);
    setSaveStatus(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveStatus(null);

    try {
      // Validate ID
      if (!editingArchetype.id || !editingArchetype.id.trim()) {
        setSaveStatus({ type: 'error', message: 'Archetype ID is required' });
        setSaving(false);
        return;
      }

      // Validate name
      if (!editingArchetype.name || !editingArchetype.name.trim()) {
        setSaveStatus({ type: 'error', message: 'Archetype name is required' });
        setSaving(false);
        return;
      }

      // Parse and validate conditions JSON
      let conditions = {};
      try {
        conditions = JSON.parse(editingArchetype.conditionsJson);
      } catch (error) {
        setSaveStatus({ type: 'error', message: 'Invalid JSON in conditions: ' + error.message });
        setSaving(false);
        return;
      }

      const validation = validateArchetypeConditions(conditions);
      if (!validation.valid) {
        setSaveStatus({ type: 'error', message: 'Invalid conditions: ' + validation.errors.join(', ') });
        setSaving(false);
        return;
      }

      // Parse famous minds
      const famousMinds = editingArchetype.famousMindsText
        .split(',')
        .map(m => m.trim())
        .filter(m => m);

      // Create archetype document
      const archetypeData = {
        name: editingArchetype.name.trim(),
        enabled: editingArchetype.enabled !== false,
        isDefault: editingArchetype.isDefault === true,
        conditions,
        reflectionQuestion: editingArchetype.reflectionQuestion || '',
        famousMinds,
        careerVectors: {
          natural: editingArchetype.careerVectors?.natural || '',
          latent: editingArchetype.careerVectors?.latent || '',
        },
        customInstructions: editingArchetype.customInstructions || '',
        updatedAt: serverTimestamp(),
      };

      // Save to Firestore
      const archetypeRef = doc(db, 'archetypes', editingArchetype.id);
      await setDoc(archetypeRef, archetypeData, { merge: true });

      setSaveStatus({ type: 'success', message: '✅ Archetype saved successfully!' });
      
      // Reload archetypes
      await loadArchetypes();
      
      // Close dialog after brief delay
      setTimeout(() => {
        setEditDialogOpen(false);
        setEditingArchetype(null);
        setSaveStatus(null);
      }, 1500);
    } catch (error) {
      console.error('Error saving archetype:', error);
      setSaveStatus({ type: 'error', message: '❌ Error: ' + error.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (archetypeId) => {
    if (!window.confirm('Are you sure you want to delete this archetype? This cannot be undone.')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'archetypes', archetypeId));
      await loadArchetypes();
      alert('Archetype deleted successfully');
    } catch (error) {
      console.error('Error deleting archetype:', error);
      alert('Error deleting archetype: ' + error.message);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 600, mb: 0.5 }}>
            🧠 Cognitive Archetypes
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
            Manage rules for matching users to cognitive patterns
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          New Archetype
        </Button>
      </Box>

      {/* Archetypes Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Reflection Question</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Default</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {archetypes.map((archetype) => (
              <TableRow key={archetype.id}>
                <TableCell>
                  <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {archetype.name}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    {archetype.id}
                  </Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: 300 }}>
                  <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                    {archetype.reflectionQuestion || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={archetype.enabled ? 'Enabled' : 'Disabled'}
                    color={archetype.enabled ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {archetype.isDefault && (
                    <Chip label="Default" color="primary" size="small" />
                  )}
                </TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => handleEdit(archetype)} size="small">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  {!archetype.isDefault && (
                    <IconButton onClick={() => handleDelete(archetype.id)} size="small" color="error">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {archetypes.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography sx={{ color: 'text.secondary' }}>
                    No archetypes found. Create one to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {editingArchetype?.id ? 'Edit Archetype' : 'Create Archetype'}
          <IconButton onClick={() => setEditDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {saveStatus && (
            <Alert severity={saveStatus.type} sx={{ mb: 2 }}>
              {saveStatus.message}
            </Alert>
          )}

          <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
            {/* ID */}
            <TextField
              label="Archetype ID"
              value={editingArchetype?.id || ''}
              onChange={(e) => setEditingArchetype({ ...editingArchetype, id: e.target.value })}
              disabled={!!editingArchetype?.id && archetypes.find(a => a.id === editingArchetype.id)}
              helperText="Unique identifier (lowercase-with-hyphens)"
              fullWidth
            />

            {/* Name */}
            <TextField
              label="Name"
              value={editingArchetype?.name || ''}
              onChange={(e) => setEditingArchetype({ ...editingArchetype, name: e.target.value })}
              fullWidth
            />

            {/* Reflection Question */}
            <TextField
              label="Reflection Question"
              value={editingArchetype?.reflectionQuestion || ''}
              onChange={(e) => setEditingArchetype({ ...editingArchetype, reflectionQuestion: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />

            {/* Famous Minds */}
            <TextField
              label="Famous Minds (comma-separated)"
              value={editingArchetype?.famousMindsText || ''}
              onChange={(e) => setEditingArchetype({ ...editingArchetype, famousMindsText: e.target.value })}
              helperText="e.g., Ray Dalio, Buckminster Fuller"
              fullWidth
            />

            {/* Career Vectors */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Natural Career"
                value={editingArchetype?.careerVectors?.natural || ''}
                onChange={(e) => setEditingArchetype({
                  ...editingArchetype,
                  careerVectors: { ...editingArchetype.careerVectors, natural: e.target.value }
                })}
                fullWidth
              />
              <TextField
                label="Latent Career"
                value={editingArchetype?.careerVectors?.latent || ''}
                onChange={(e) => setEditingArchetype({
                  ...editingArchetype,
                  careerVectors: { ...editingArchetype.careerVectors, latent: e.target.value }
                })}
                fullWidth
              />
            </Box>

            {/* Conditions (JSON) */}
            <TextField
              label="Conditions (JSON)"
              value={editingArchetype?.conditionsJson || ''}
              onChange={(e) => setEditingArchetype({ ...editingArchetype, conditionsJson: e.target.value })}
              multiline
              rows={6}
              helperText="Define matching rules in JSON format"
              fullWidth
              sx={{ fontFamily: 'monospace' }}
            />

            {/* Custom Instructions */}
            <TextField
              label="Custom Instructions"
              value={editingArchetype?.customInstructions || ''}
              onChange={(e) => setEditingArchetype({ ...editingArchetype, customInstructions: e.target.value })}
              multiline
              rows={4}
              helperText="Additional prompt instructions for this archetype"
              fullWidth
            />

            {/* Toggles */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editingArchetype?.enabled !== false}
                    onChange={(e) => setEditingArchetype({ ...editingArchetype, enabled: e.target.checked })}
                  />
                }
                label="Enabled"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editingArchetype?.isDefault === true}
                    onChange={(e) => setEditingArchetype({ ...editingArchetype, isDefault: e.target.checked })}
                  />
                }
                label="Default (fallback)"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ArchetypesManager;

